import type { SuretyAccountId } from "@inrev/common";
import { useGetAccountIndemnityAgreementFileUrl } from "@portal/api/account/agent";
import { Banner, Button, Text } from "@portal/components/primitives";
import type { RefObject } from "react";
import { indemnityAgreementSignatureMethodTypeDisplayMap } from "../../../../../../../../constants";
import { InProgressIndemnityAgreement } from "../../../../../../../account/types";
import { IndemnityAgreementSectionLayout } from "../layout/IndemnityAgreementLayout";
import { AccountIndemnitorList } from "./AccountIndemnitorList";
import { IndemnityAgreementFileUpload } from "./IndemnityAgreementFileUpload";

export const ElectronicToPhysicalModal = ({
	indemnityAgreement,
	suretyAccountId,
	domReadyRef,
}: {
	indemnityAgreement: InProgressIndemnityAgreement;
	suretyAccountId: SuretyAccountId;
	domReadyRef: RefObject<HTMLDivElement | null>;
}) => {
	const { downloadIndemnityAgreement, indemnityAgreementFileUrlLoading } =
		useGetAccountIndemnityAgreementFileUrl({
			accountId: suretyAccountId,
			indemnityAgreementId: indemnityAgreement.id,
			asDownload: true,
		});

	if (indemnityAgreement.status === "incomplete" || !indemnityAgreement.id) return null;

	const signatureMethod = (
		<Text type="body" color="primary">
			{indemnityAgreementSignatureMethodTypeDisplayMap.physical}
		</Text>
	);

	const body = (
		<div className="flex flex-col space-y-[30px]">
			<div className="flex flex-col space-y-5">
				<Banner tone="neutral">
					<Banner.Content>
						<Text type="body" color="primary">
							Uploading a physical copy will void the current electronic document upon submission
						</Text>
					</Banner.Content>
				</Banner>
				<IndemnityAgreementFileUpload
					indemnityAgreementId={indemnityAgreement.id}
					suretyAccountId={suretyAccountId}
					indemnityAgreementStatus={indemnityAgreement.status}
					additionalButtons={
						<Button
							onClick={() => downloadIndemnityAgreement()}
							color="secondary"
							variant="outline"
							loading={indemnityAgreementFileUrlLoading}
						>
							Download Indemnity Agreement
						</Button>
					}
				/>
			</div>
		</div>
	);

	return (
		<div className="flex justify-center items-center w-[100vw] h-[100vh] sm:w-[60vw] sm:h-[60vw] md:h-[50vh] md:w-[50vw]">
			<IndemnityAgreementSectionLayout
				domReadyRef={domReadyRef}
				requiredIndemnitors={
					<AccountIndemnitorList
						indemnitors={indemnityAgreement.indemnitors}
						showESignatureDetails={false}
					/>
				}
				signatureMethod={signatureMethod}
				body={body}
				bodyTitle="Indemnity Agreement"
				bodyTestId="agent-account-indemnity-agreement-card"
			/>
		</div>
	);
};
